<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { DatePickerProps } from 'naive-ui';
import { gaiaNeutral, gaiaMotion } from '@/components/design/themes/tokens';
import { IconCalendar } from '@/components/design/icons';
import GaiaInput from './GaiaInput.vue';
import GaiaPopover from '../GaiaPopover.vue';
import GaiaText from '../GaiaText.vue';
import {
  dateFromIso,
  describeDateInput,
  formatDateInputDisplay,
  isoFromDate,
  parseDateInput,
} from '@/utils/dateInput';

/**
 * A date the person can TYPE, with the calendar still one click away.
 *
 * A picker alone makes a person hunt for a date they already know, and the
 * usual fix — a plain text box — trades that for the day/month question
 * (is 3/2 March or February?). This field answers the question instead of
 * asking it:
 *
 * - typing is forgiving (`3 feb 2026`, `3/2/2026`, `2026-02-03`, `today`),
 *   all-numeric read day first;
 * - while typing, the date is echoed in full underneath — weekday, day,
 *   month by name, year — so a misread is caught on the spot;
 * - once committed the input shows `03 Feb 2026`, a written month that can't
 *   be taken for a day, whatever was typed to get there;
 * - the calendar button opens the same panel for browsing/choosing.
 *
 * Value in and out is an ISO calendar date (`"YYYY-MM-DD"`) — the shape the
 * forms and the template renderer already use.
 */
interface Props {
  /** ISO calendar date, `"YYYY-MM-DD"`, or null when unanswered. */
  value: string | null;
  placeholder?: string;
  status?: 'success' | 'warning' | 'error';
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  clearable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: undefined,
  status: undefined,
  disabled: false,
  size: undefined,
  clearable: true,
});

const emit = defineEmits<{
  'update:value': [value: string | null];
  /** Enter with the value already committed — lets a form advance focus. */
  enter: [event: KeyboardEvent];
  blur: [];
}>();

const text = ref<string>(props.value ? formatDateInputDisplay(props.value) : '');
const focused = ref(false);
const panelOpen = ref(false);

// An external change (draft restore, a sibling field, the panel) rewrites the
// input — unless the person is typing in it, whose keystrokes always win.
watch(
  () => props.value,
  incoming => {
    if (focused.value) return;
    text.value = incoming ? formatDateInputDisplay(incoming) : '';
  }
);

/** What the current text means, recomputed on every keystroke. */
const parsed = computed(() => {
  const raw = text.value.trim();
  return raw.length === 0 ? null : parseDateInput(raw);
});

/**
 * Raised only when the person LEAVES text we can't read — half a date is the
 * normal state of a field being typed into ("3" is not yet wrong), so the red
 * waits for them to be done. Typing again clears it.
 */
const unreadable = ref(false);
watch(text, () => {
  unreadable.value = false;
});

/** Typed nonsense reads as an error even when the form itself is happy. */
const inputStatus = computed(() => (unreadable.value ? 'error' : props.status));

/**
 * The line under the field: the date in words as it's typed, how to write one
 * before anything is, and what went wrong when the text can't be read. It
 * stays out of the way once the field is settled — the value itself, with its
 * written month, already says what it is.
 */
const hint = computed<{ text: string; tone: 'subtle' | 'alert' } | null>(() => {
  if (unreadable.value) {
    return { text: "Can't read that date — try 3 Feb 2026, 3/2/2026 or 2026-02-03", tone: 'alert' };
  }
  if (!focused.value) return null;
  if (parsed.value) return { text: describeDateInput(parsed.value.iso), tone: 'subtle' };
  return {
    text: 'Type a date (3 Feb 2026 · 3/2/2026 · today) or pick one from the calendar',
    tone: 'subtle',
  };
});

/**
 * Settle the input: an empty box clears the answer, readable text becomes the
 * canonical `03 Feb 2026`, and text we can't read is left exactly as typed —
 * reverting it would throw away the work and hide the mistake, while the hint
 * below says what's wrong.
 */
function commit(): void {
  const raw = text.value.trim();
  if (raw.length === 0) {
    text.value = '';
    if (props.value !== null) emit('update:value', null);
    return;
  }
  const result = parseDateInput(raw);
  if (!result) {
    unreadable.value = true;
    return;
  }
  text.value = formatDateInputDisplay(result.iso);
  if (result.iso !== props.value) emit('update:value', result.iso);
}

function handleBlur(): void {
  focused.value = false;
  commit();
  emit('blur');
}

function handleEnter(event: KeyboardEvent): void {
  commit();
  emit('enter', event);
}

function handleClear(): void {
  text.value = '';
  emit('update:value', null);
}

// --- calendar panel ------------------------------------------------------
//
// The panel speaks epoch millis; the field speaks ISO. Translating here keeps
// the second representation out of the component's state.
const panelValue = computed<number | null>(() => {
  const date = props.value ? dateFromIso(props.value) : null;
  return date ? date.getTime() : null;
});

function handlePick(ms: number | null): void {
  panelOpen.value = false;
  unreadable.value = false;
  if (ms === null) {
    handleClear();
    return;
  }
  const iso = isoFromDate(new Date(ms));
  text.value = formatDateInputDisplay(iso);
  if (iso !== props.value) emit('update:value', iso);
}

/** Just "Now" under the panel — clearing is the input's own × icon. */
const PANEL_ACTIONS: DatePickerProps['actions'] = ['now'];

const iconColor = gaiaNeutral.textFaint;
const iconColorHover = gaiaNeutral.text;
const quickFade = gaiaMotion.fade;
</script>

<template>
  <!-- `gaia-date-field` marks the widget for anything walking the form (the
       product tour skips dates) — from the outside this is now an n-input. -->
  <div class="gaia-date-field">
    <gaia-input
      v-model:value="text"
      :status="inputStatus"
      :disabled="disabled"
      :size="size"
      :placeholder="placeholder ?? 'e.g. 3 Feb 2026'"
      :clearable="clearable"
      @focus="focused = true"
      @blur="handleBlur"
      @keyup.enter="handleEnter"
      @clear="handleClear"
    >
      <template #suffix>
        <gaia-popover v-model:show="panelOpen" placement="bottom-end" :disabled="disabled">
          <template #trigger>
            <button
              type="button"
              tabindex="-1"
              class="calendar-trigger"
              :disabled="disabled"
              aria-label="Open calendar"
            >
              <n-icon :size="16" :component="IconCalendar" />
            </button>
          </template>
          <n-date-picker
            panel
            type="date"
            :value="panelValue"
            :actions="PANEL_ACTIONS"
            @update:value="handlePick"
          />
        </gaia-popover>
      </template>
    </gaia-input>

    <gaia-text v-if="hint" size="meta" :tone="hint.tone" aria-live="polite">
      {{ hint.text }}
    </gaia-text>
  </div>
</template>

<style scoped>
.gaia-date-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

/* A bare glyph inside the input's suffix: everything a <button> brings by
   default (surface, border, padding, focus ring) has to come off, which no
   naive component expresses — n-button always draws a control. */
.calendar-trigger {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: v-bind(iconColor);
  cursor: pointer;
  transition: color v-bind(quickFade);
}

.calendar-trigger:hover {
  color: v-bind(iconColorHover);
}

.calendar-trigger:disabled {
  cursor: not-allowed;
}
</style>
